/**
 * Defender Kinematics
 *
 * The "how a defender moves" half of the reaction engine, separated from the "what he
 * decides" half that lives in the read policies (reactionEngine.ts).
 *
 * Read policies pick a TARGET and a DESIRED SPEED each tick. This module decides what the
 * defender's body can actually do about that, subject to five physical constraints:
 *
 *   1. Reaction latency  — he acts on what he saw ~120ms ago, never on the current frame.
 *   2. Acceleration      — he builds and sheds speed at a finite rate; no instant velocity.
 *   3. Transition cost   — changing technique (planting, flipping hips) costs him time.
 *   4. Technique         — backpedal/shuffle/turn-and-run/drive have different speed
 *                          ceilings and wildly different costs to come out of.
 *   5. Leverage          — he plays a shade on his key, not a mirror of him.
 *
 * Together these produce the behavior a coach actually recognizes: a corner in a backpedal
 * drives on a hitch, while the same corner turned and running deep is beaten by a comeback —
 * not because anything told him to be, but because flipping back out of `turnAndRun` costs
 * him 400ms he does not have.
 *
 * Pure module: no React, no side effects, no randomness. Safe to unit test.
 */

import {
  Position,
  Observation,
  Technique,
  TechniqueProfile,
  KinematicsParams,
  DefenderMotionState,
} from '../types';

/**
 * Default movement envelopes per technique, calibrated to this app's units
 * (1 yard ≈ 3 units, so 33 units/sec ≈ 11 yd/sec ≈ a fast defensive back at full sprint).
 *
 * The speed ordering is the whole point: a backpedal is roughly two-thirds of sprint speed
 * and a shuffle is slower still, which is precisely why defenders must eventually commit and
 * turn to run with a vertical rather than backpedaling forever.
 */
export const DEFAULT_TECHNIQUE_PROFILES: Record<Technique, TechniqueProfile> = {
  // Facing the LOS, gaining depth. Can't match a sprinting receiver, but is loaded to break forward.
  backpedal: { maxSpeed: 22, accel: 55, decel: 75 },
  // Lateral, underneath, holding depth. Slowest, but the most agile short-area technique.
  shuffle: { maxSpeed: 20, accel: 60, decel: 80 },
  // Hips flipped, running downfield. Full speed; see TECHNIQUE_TRANSITION_MS for the cost of leaving it.
  turnAndRun: { maxSpeed: 33, accel: 50, decel: 65 },
  // Sprinting downhill at the key/ball. Full speed.
  drive: { maxSpeed: 33, accel: 58, decel: 75 },
};

/*
 * A note on why these accelerations are high (~0.6s to top speed rather than a human's ~2s).
 *
 * Receivers in this app run SCRIPTED routes: `routeRules` waypoints put a WR at ~29.6 units/sec
 * from t=0 with no ramp at all. Modeling defender acceleration faithfully against an offense that
 * has none is not realism, it is a handicap — a corner starting from rest would lose ~18 units to
 * a go route before reaching top speed and could never recover them.
 *
 * The realism that actually matters for coverage lives in the TRANSITIONS — the cost of flipping
 * hips, of driving downhill out of a backpedal, of changing direction with momentum. Those are
 * preserved in full by TECHNIQUE_TRANSITION_MS and the speed ceilings. The initial burst is
 * deliberately generous to keep the two sides on the same footing.
 *
 * If routes ever gain their own acceleration model, lower these toward ~25 units/sec² and the
 * defenders will still behave correctly — only the ramp changes.
 */

/**
 * Time cost (ms) of changing technique — the footwork tax.
 *
 * `TECHNIQUE_TRANSITION_MS[from][to]`. The asymmetry is the football: breaking FORWARD out of
 * a backpedal is nearly free (80ms — that is what a backpedal is *for*), while coming back
 * downhill after you have already turned and run is the most expensive move on the field
 * (400ms), because you must decelerate, plant, and re-accelerate the other way.
 */
export const TECHNIQUE_TRANSITION_MS: Record<Technique, Record<Technique, number>> = {
  backpedal: { backpedal: 0, shuffle: 60, turnAndRun: 250, drive: 80 },
  shuffle: { backpedal: 60, shuffle: 0, turnAndRun: 220, drive: 100 },
  turnAndRun: { backpedal: 350, shuffle: 300, turnAndRun: 0, drive: 400 },
  drive: { backpedal: 300, shuffle: 200, turnAndRun: 350, drive: 0 },
};

/**
 * Baseline kinematics every shipped defender starts from. Individual params objects override
 * `leverage` / `sideSign` (and occasionally latency) for their position.
 */
export const DEFAULT_KINEMATICS: KinematicsParams = {
  // 120ms = exactly 2 simulation ticks. A cold visual-choice reaction is ~200ms, but a defender
  // reading a route he has practiced against all week is ANTICIPATING, not reacting from
  // scratch, which lands a trained DB in the 120-150ms range.
  //
  // Note the tick grid quantizes this: `round(ms / SIM_DT_MS)`. At a 60ms tick the only
  // reachable delays are 180 / 120 / 60ms, and 90-150 all collapse to 120. 120 is chosen so the
  // configured value and the effective value are the same number.
  reactionLatencyMs: 120,
  leverage: 'headUp',
  leverageUnits: 0,
  sideSign: -1,
  transitionSpeedFactor: 0.45, // held to 45% of pace while planting/flipping
};

/** Resolve a technique's movement envelope, applying any per-defender override. */
export function getTechniqueProfile(technique: Technique, params: KinematicsParams): TechniqueProfile {
  return params.profiles?.[technique] ?? DEFAULT_TECHNIQUE_PROFILES[technique];
}

/**
 * The lateral offset (field units) a defender's leverage puts him at relative to his key.
 *
 * "Outside" always means toward this defender's own sideline, so the sign flips by side:
 * a left-side corner's outside is decreasing x, a right-side corner's is increasing x.
 */
export function leverageOffsetX(params: KinematicsParams): number {
  if (params.leverage === 'headUp') return 0;
  const towardSideline = params.sideSign * params.leverageUnits;
  return params.leverage === 'outside' ? towardSideline : -towardSideline;
}

/**
 * Apply this defender's leverage shade to a key position, producing the spot he actually
 * wants to occupy rather than the receiver's exact coordinates.
 */
export function applyLeverage(keyPosition: Position, params: KinematicsParams): Position {
  return { x: keyPosition.x + leverageOffsetX(params), y: keyPosition.y };
}

/**
 * The observation a defender is actually reacting to at feed index `i` — the one from
 * `reactionLatencyMs` ago, clamped to the start of the play.
 *
 * Note this shifts WHEN a defender reacts, never WHAT he concludes: the delayed feed is the
 * same sequence of positions, so break detection sees identical evidence in identical order,
 * just later. That is why adding latency does not perturb any coverage's read table.
 */
export function perceivedIndex(i: number, dtMs: number, params: KinematicsParams): number {
  const lookback = Math.round(params.reactionLatencyMs / dtMs);
  return Math.max(0, i - lookback);
}

/** Convenience: the delayed observation pair (previous, current) a defender perceives at index `i`. */
export function perceivedObservations(
  feed: Observation[],
  i: number,
  dtMs: number,
  params: KinematicsParams
): { prev: Observation; cur: Observation } {
  const idx = perceivedIndex(i, dtMs, params);
  const prevIdx = Math.max(0, idx - 1);
  return { prev: feed[prevIdx], cur: feed[idx] };
}

/**
 * Infer the technique a defender would be in from where he is trying to go and how fast.
 *
 * Read policies that have an explicit opinion (a corner carrying a vertical is in `turnAndRun`;
 * an apex jumping a route is in `drive`) should pass their technique to {@link stepDefender}
 * directly. This is the fallback for movement that is just "get to a spot": it picks the
 * technique a real defender would use for that direction and pace, so every policy gets
 * plausible physics without hand-authoring a technique for every branch.
 *
 * - Gaining depth at sprint pace → `turnAndRun` (you cannot backpedal that fast).
 * - Gaining depth at a controlled pace → `backpedal` (facing the LOS, able to break forward).
 * - Coming downhill toward the LOS → `drive`.
 * - Mostly lateral at a controlled pace → `shuffle`.
 */
export function inferTechnique(
  from: Position,
  to: Position,
  desiredSpeed: number,
  params: KinematicsParams = DEFAULT_KINEMATICS
): Technique {
  const dy = to.y - from.y; // negative = deeper downfield
  const dx = to.x - from.x;
  const gainingDepth = dy < 0;
  const mostlyLateral = Math.abs(dx) > Math.abs(dy);

  if (gainingDepth) {
    // Above what a backpedal can sustain, he must have turned and opened his hips.
    return desiredSpeed > getTechniqueProfile('backpedal', params).maxSpeed ? 'turnAndRun' : 'backpedal';
  }
  if (dy > 0) return 'drive'; // working back toward the LOS
  return mostlyLateral ? 'shuffle' : 'backpedal';
}

/** A defender standing still in the given technique, ready to be integrated forward. */
export function createMotionState(start: Position, technique: Technique): DefenderMotionState {
  return {
    position: { ...start },
    velocity: { x: 0, y: 0 },
    technique,
    transitionRemainingMs: 0,
  };
}

/**
 * Advance a defender one tick toward `target`.
 *
 * Applies, in order: the technique-change tax, the technique's speed ceiling, arrival braking
 * (so he settles onto a landmark instead of oscillating around it), and the acceleration
 * limit. The returned state is new — the input is never mutated.
 *
 * @param desiredSpeed What the read policy WANTS him to move at. Treated as a request; the
 *                     technique's `maxSpeed` and the transition tax both cap it.
 * @param technique    The technique the policy wants him in this tick. Changing it charges
 *                     the transition cost automatically.
 */
export function stepDefender(
  state: DefenderMotionState,
  target: Position,
  desiredSpeed: number,
  technique: Technique,
  dtSec: number,
  params: KinematicsParams
): DefenderMotionState {
  // --- 1. Technique change: charge the footwork tax. ---
  let transitionRemainingMs = state.transitionRemainingMs;
  if (technique !== state.technique) {
    transitionRemainingMs = TECHNIQUE_TRANSITION_MS[state.technique][technique];
  }
  const inTransition = transitionRemainingMs > 0;

  const profile = getTechniqueProfile(technique, params);

  // --- 2. Speed ceiling: the technique caps the policy's request, and planting caps it further. ---
  const throttle = inTransition ? params.transitionSpeedFactor : 1;
  const cappedSpeed = Math.min(desiredSpeed, profile.maxSpeed) * throttle;

  // --- 3. Arrival braking: shed speed approaching the target so he settles rather than orbits. ---
  const dx = target.x - state.position.x;
  const dy = target.y - state.position.y;
  const distance = Math.hypot(dx, dy);

  let desiredVx = 0;
  let desiredVy = 0;
  if (distance > 1e-6) {
    // The fastest he can be going and still stop exactly on the target: v = sqrt(2 * a * d).
    const brakingSpeed = Math.sqrt(2 * profile.decel * distance);
    const targetSpeed = Math.min(cappedSpeed, brakingSpeed);
    desiredVx = (dx / distance) * targetSpeed;
    desiredVy = (dy / distance) * targetSpeed;
  }

  // --- 4. Acceleration limit: velocity changes at a finite rate, and stopping beats starting. ---
  const currentSpeed = Math.hypot(state.velocity.x, state.velocity.y);
  const desiredSpeedMag = Math.hypot(desiredVx, desiredVy);
  const rate = desiredSpeedMag >= currentSpeed ? profile.accel : profile.decel;
  const maxDeltaV = rate * dtSec;

  let dvx = desiredVx - state.velocity.x;
  let dvy = desiredVy - state.velocity.y;
  const deltaVMag = Math.hypot(dvx, dvy);
  if (deltaVMag > maxDeltaV && deltaVMag > 1e-9) {
    const scale = maxDeltaV / deltaVMag;
    dvx *= scale;
    dvy *= scale;
  }

  let vx = state.velocity.x + dvx;
  let vy = state.velocity.y + dvy;

  // Never exceed the technique's ceiling, even mid-transition.
  const newSpeed = Math.hypot(vx, vy);
  if (newSpeed > cappedSpeed && newSpeed > 1e-9) {
    const scale = cappedSpeed / newSpeed;
    vx *= scale;
    vy *= scale;
  }

  // --- 5. Integrate, never stepping past the target (belt-and-braces against overshoot). ---
  let stepX = vx * dtSec;
  let stepY = vy * dtSec;
  const stepMag = Math.hypot(stepX, stepY);
  if (stepMag > distance && stepMag > 1e-9) {
    const scale = distance / stepMag;
    stepX *= scale;
    stepY *= scale;
  }

  return {
    position: { x: state.position.x + stepX, y: state.position.y + stepY },
    velocity: { x: vx, y: vy },
    technique,
    transitionRemainingMs: Math.max(0, transitionRemainingMs - dtSec * 1000),
  };
}
